import {
  boardRectToScreen,
  embedSrc,
  rectIntersects,
  visibleRect,
  type EmbedNode,
} from '@kanvas/board-core';
import { useMemo } from 'react';
import type { BoardDoc } from '../collab/BoardDoc';
import { useNodes } from '../collab/hooks';
import { useEditor } from '../state/store';

/** Below this zoom an iframe is unreadable, so the canvas placeholder stands in. */
const MIN_INTERACTIVE_ZOOM = 0.25;

/**
 * Live embeds.
 *
 * Iframes cannot be drawn into a canvas, so embedded content is a DOM layer
 * positioned over the board. Pointer events are disabled until the node is
 * selected: otherwise the iframe would swallow every drag that crosses it and
 * the board would feel broken.
 */
export function EmbedLayer({ doc }: { doc: BoardDoc }) {
  const nodes = useNodes(doc);
  const viewport = useEditor((s) => s.viewport);
  const selection = useEditor((s) => s.selection);

  const embeds = useMemo(() => {
    if (viewport.zoom < MIN_INTERACTIVE_ZOOM) return [];
    const area = visibleRect(viewport);
    return nodes.filter(
      (n): n is EmbedNode =>
        n.type === 'embed' &&
        rectIntersects(area, { x: n.x, y: n.y, w: n.w, h: n.h }),
    );
  }, [nodes, viewport]);

  if (embeds.length === 0) return null;

  return (
    <div className="embed-layer">
      {embeds.map((node) => {
        const r = boardRectToScreen(viewport, { x: node.x, y: node.y, w: node.w, h: node.h });
        const active = selection.includes(node.id);
        return (
          <div
            key={node.id}
            className={`embed-frame ${active ? 'is-active' : ''}`}
            style={{
              left: `${r.x}px`,
              top: `${r.y}px`,
              width: `${r.w}px`,
              height: `${r.h}px`,
              transform: node.rotation !== 0 ? `rotate(${node.rotation}rad)` : undefined,
            }}
          >
            <iframe
              src={embedSrc(node)}
              title={node.title || node.url}
              loading="lazy"
              referrerPolicy="no-referrer"
              sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
              allow="autoplay; fullscreen; picture-in-picture"
              style={{ pointerEvents: active ? 'auto' : 'none' }}
            />
            {!active && <div className="embed-shield" />}
          </div>
        );
      })}
    </div>
  );
}
